/**
 * PeopleTree - Premium GSAP Animation Utilities
 * Inspired by Axtra theme with custom enhancements
 */

const gsap = window.gsap;
const ScrollTrigger = window.ScrollTrigger;

// Register plugin
if (gsap && ScrollTrigger) {
  gsap.registerPlugin(ScrollTrigger);
}

/**
 * Initialize all scroll-triggered animations on the page
 */
export const initScrollAnimations = () => {
  if (!gsap || !ScrollTrigger) return;

  // Fade up elements
  gsap.utils.toArray('.anim-fade-up').forEach((el) => {
    gsap.fromTo(el,
      { y: 60, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' },
      }
    );
  });

  // Fade up with stagger (for grids)
  gsap.utils.toArray('.anim-stagger-parent').forEach((parent) => {
    const children = parent.querySelectorAll('.anim-stagger-child');
    gsap.fromTo(children,
      { y: 80, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: 'power3.out',
        scrollTrigger: { trigger: parent, start: 'top 85%', toggleActions: 'play none none none' },
      }
    );
  });

  // Scale reveal (images)
  gsap.utils.toArray('.anim-img-reveal').forEach((el) => {
    gsap.fromTo(el,
      { scale: 1.2, opacity: 0 },
      {
        scale: 1, opacity: 1, duration: 1.4, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
      }
    );
  });

  // Slide from left
  gsap.utils.toArray('.anim-slide-left').forEach((el) => {
    gsap.fromTo(el,
      { x: -100, opacity: 0 },
      {
        x: 0, opacity: 1, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
      }
    );
  });

  // Slide from right
  gsap.utils.toArray('.anim-slide-right').forEach((el) => {
    gsap.fromTo(el,
      { x: 100, opacity: 0 },
      {
        x: 0, opacity: 1, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
      }
    );
  });

  // Line reveal (dividers/borders)
  gsap.utils.toArray('.anim-line-reveal').forEach((el) => {
    gsap.fromTo(el,
      { scaleX: 0 },
      {
        scaleX: 1, duration: 1.2, ease: 'power3.inOut', transformOrigin: 'left center',
        scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' },
      }
    );
  });

  // Parallax elements
  gsap.utils.toArray('.anim-parallax').forEach((el) => {
    const speed = el.dataset.speed || 0.2;
    gsap.to(el, {
      y: () => -150 * parseFloat(speed),
      ease: 'none',
      scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
    });
  });

  // Rotate on scroll
  gsap.utils.toArray('.anim-rotate-scroll').forEach((el) => {
    gsap.to(el, {
      rotation: 360,
      ease: 'none',
      scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 1 },
    });
  });

  // Counter animation
  gsap.utils.toArray('.anim-counter').forEach((el) => {
    const target = parseInt(el.dataset.count) || 0;
    const obj = { val: 0 };
    gsap.to(obj, {
      val: target,
      duration: 2,
      ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
      onUpdate: () => { el.textContent = Math.round(obj.val); },
    });
  });

  // Text char reveal (Axtra-style)
  gsap.utils.toArray('.anim-text-reveal').forEach((el) => {
    const text = el.textContent;
    el.innerHTML = '';
    const words = text.split(' ');
    words.forEach((word, wi) => {
      const wordSpan = document.createElement('span');
      wordSpan.style.display = 'inline-block';
      wordSpan.style.overflow = 'hidden';
      word.split('').forEach((char) => {
        const charSpan = document.createElement('span');
        charSpan.textContent = char;
        charSpan.style.display = 'inline-block';
        charSpan.style.transform = 'translateY(110%)';
        charSpan.className = 'anim-char';
        wordSpan.appendChild(charSpan);
      });
      el.appendChild(wordSpan);
      if (wi < words.length - 1) {
        const space = document.createElement('span');
        space.innerHTML = '&nbsp;';
        space.style.display = 'inline-block';
        el.appendChild(space);
      }
    });

    gsap.to(el.querySelectorAll('.anim-char'), {
      y: '0%', duration: 0.6, stagger: 0.02, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
    });
  });

  // Word reveal
  gsap.utils.toArray('.anim-word-reveal').forEach((el) => {
    const text = el.textContent.replace(/ /g, ' ').trim();
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0) return;

    el.innerHTML = '';
    el.style.display = 'flex';
    el.style.flexWrap = 'wrap';
    el.style.columnGap = '0.3em';

    words.forEach((word) => {
      const wrapper = document.createElement('span');
      wrapper.style.overflow = 'hidden';
      wrapper.style.display = 'inline-block';
      const inner = document.createElement('span');
      inner.textContent = word;
      inner.style.display = 'inline-block';
      inner.style.transform = 'translateY(105%)';
      inner.className = 'anim-word';
      wrapper.appendChild(inner);
      el.appendChild(wrapper);
    });

    gsap.to(el.querySelectorAll('.anim-word'), {
      y: '0%', duration: 0.7, stagger: 0.05, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
    });
  });

  // Marquee / infinite horizontal scroll
  gsap.utils.toArray('.anim-marquee').forEach((el) => {
    const inner = el.querySelector('.anim-marquee__inner');
    if (!inner) return;
    const width = inner.scrollWidth / 2;
    gsap.to(inner, {
      x: -width,
      ease: 'none',
      duration: 20,
      repeat: -1,
    });
  });
};

/**
 * Hero slider GSAP animation
 */
export const initHeroSlider = (slides, setActiveSlide) => {
  if (!gsap) return;

  let current = 0;
  const total = slides.length;

  const nextSlide = () => {
    current = (current + 1) % total;
    setActiveSlide(current);
  };

  const interval = setInterval(nextSlide, 5000);
  return () => clearInterval(interval);
};

/**
 * Magnetic button effect
 */
export const initMagneticButtons = () => {
  if (!gsap) return;

  document.querySelectorAll('.btn-magnetic').forEach((btn) => {
    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      gsap.to(btn, { x: x * 0.3, y: y * 0.3, duration: 0.3, ease: 'power2.out' });
    });
    btn.addEventListener('mouseleave', () => {
      gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.5)' });
    });
  });
};

/**
 * Smooth scroll reveal for section headers
 */
export const animateSectionHeader = (el) => {
  if (!gsap || !ScrollTrigger || !el) return;

  const label = el.querySelector('.section-header__label');
  const title = el.querySelector('.section-header__title');
  const desc = el.querySelector('.section-header__desc');

  const tl = gsap.timeline({
    scrollTrigger: { trigger: el, start: 'top 85%', toggleActions: 'play none none none' },
  });

  if (label) tl.fromTo(label, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' });
  if (title) tl.fromTo(title, { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, '-=0.3');
  if (desc) tl.fromTo(desc, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, '-=0.4');
};

/**
 * Cleanup all ScrollTrigger instances
 */
export const cleanupAnimations = () => {
  if (ScrollTrigger) {
    ScrollTrigger.getAll().forEach((t) => t.kill());
  }
};
